import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle,
  Building2,
  Clock,
  GitBranch,
  MapPin,
  Shield,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const ONLINE_THRESHOLD_MS = 5 * 60 * 1000;

export const ENTITY_TYPE_CONFIG = {
  user: { label: 'User', className: 'bg-sky-100 text-sky-800 border-sky-200' },
  responder: { label: 'Responder', className: 'bg-blue-100 text-blue-800 border-blue-200' },
  team: { label: 'Team', className: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
  incident: { label: 'Incident', className: 'bg-red-100 text-red-800 border-red-200' },
  resource: { label: 'Resource', className: 'bg-teal-100 text-teal-800 border-teal-200' },
  shelter: { label: 'Shelter', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  location: { label: 'Location', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  vehicle: { label: 'Vehicle', className: 'bg-orange-100 text-orange-800 border-orange-200' },
};

const STATUS_CONFIG = {
  active: { label: 'Active', className: 'bg-green-100 text-green-700 border-green-200' },
  online: { label: 'Online', className: 'bg-green-100 text-green-700 border-green-200' },
  live: { label: 'Live', className: 'bg-green-100 text-green-700 border-green-200' },
  resolved: { label: 'Resolved', className: 'bg-slate-100 text-slate-600 border-slate-200' },
  offline: { label: 'Offline', className: 'bg-gray-100 text-gray-500 border-gray-200' },
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  assigned: { label: 'Assigned', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  unassigned: { label: 'Unassigned', className: 'bg-gray-100 text-gray-500 border-gray-200' },
  'in-duty': { label: 'In Duty', className: 'bg-green-100 text-green-700 border-green-200' },
  'not-in-duty': { label: 'Not In Duty', className: 'bg-red-100 text-red-700 border-red-200' },
  'unknown-duty': { label: 'Unknown', className: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const SEVERITY_CONFIG = {
  high: { label: 'High', className: 'bg-red-100 text-red-700 border-red-200' },
  critical: { label: 'Critical', className: 'bg-red-100 text-red-700 border-red-200' },
  medium: { label: 'Medium', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  moderate: { label: 'Moderate', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  low: { label: 'Low', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  minor: { label: 'Minor', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
};

export const formatDateTime = (value) => {
  if (value == null || value === '') return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export const formatCoords = (lat, lng) => {
  const la = parseFloat(lat);
  const ln = parseFloat(lng);
  if (Number.isNaN(la) || Number.isNaN(ln)) return null;
  return `${la.toFixed(5)}, ${ln.toFixed(5)}`;
};

export const formatTimeAgo = (timestamp) => {
  if (timestamp == null || timestamp === '') return null;
  const ts = new Date(timestamp).getTime();
  if (Number.isNaN(ts)) return null;
  const seconds = Math.floor((Date.now() - ts) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return formatDateTime(timestamp);
};

export const isUserOnline = (timestamp) => {
  if (timestamp == null) return false;
  const ts = new Date(timestamp).getTime();
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts < ONLINE_THRESHOLD_MS;
};

export const isUserLive = (data) => {
  if (!data) return false;
  if (data.status === 'offline') return false;
  return isUserOnline(data.timestamp);
};

const looksLikeId = (value) => {
  const str = String(value).trim();
  return /^[a-f0-9-]{24,}$/i.test(str)
    || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
};

export const buildDepartmentNameMap = (departments) => {
  const map = new Map();
  for (const dept of departments || []) {
    const id = dept?.id;
    const name = dept?.department_name || dept?.name;
    if (id && name) map.set(String(id), name);
  }
  return map;
};

export const resolveDepartmentName = (value, departmentNameMap) => {
  if (value == null || value === '') return null;
  const str = String(value).trim();
  if (!str) return null;
  const resolved = departmentNameMap?.get(str);
  if (resolved) return resolved;
  if (looksLikeId(str)) return null;
  return str;
};

export const getDutyStatus = (data) => {
  if (data?.is_active === true) return { key: 'in-duty', label: 'In Duty' };
  if (data?.is_active === false) return { key: 'not-in-duty', label: 'Not In Duty' };
  return { key: 'unknown-duty', label: 'Unknown' };
};

export const getUserDutyFilterKey = (data) => getDutyStatus(data).key;

export const getDepartmentDisplay = (data, departmentNameMap) => {
  if (!data) return null;
  if (data.department_name && !looksLikeId(data.department_name)) {
    return data.department_name;
  }
  const candidates = [data.department_unit, data.department];
  if (Array.isArray(data.departments_units)) {
    candidates.push(...data.departments_units);
  }
  for (const candidate of candidates) {
    const name = resolveDepartmentName(candidate, departmentNameMap);
    if (name) return name;
  }
  return null;
};

const getIncidentStatusKey = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'closed' || s === 'resolved') return 'resolved';
  if (s === 'pending' || s === 'open') return 'pending';
  return 'active';
};

const getSeverityKey = (severity) => {
  const s = (severity || '').toLowerCase();
  if (s === 'critical' || s === 'high') return 'high';
  if (s === 'moderate' || s === 'medium') return 'medium';
  if (s === 'minor' || s === 'low') return 'low';
  return s || null;
};

const getLocationLabel = (data) => {
  if (data?.map_projection?.location_name) return data.map_projection.location_name;
  if (Array.isArray(data.location_names) && data.location_names.length > 0) {
    return data.location_names.filter(Boolean).join(', ');
  }
  if (typeof data.location_name === 'string' && data.location_name.trim()) {
    return data.location_name.trim();
  }
  return null;
};

export const EntityTypeBadge = ({ entityType }) => {
  const config = ENTITY_TYPE_CONFIG[entityType] || ENTITY_TYPE_CONFIG.incident;
  return (
    <Badge variant="outline" className={`text-[10px] font-semibold px-1.5 py-0 h-5 border ${config.className}`}>
      {config.label}
    </Badge>
  );
};

export const StatusBadge = ({ statusKey, label }) => {
  const config = STATUS_CONFIG[statusKey];
  if (!config) return null;
  return (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 border ${config.className}`}>
      {label || config.label}
    </Badge>
  );
};

export const SeverityBadge = ({ severity }) => {
  const key = getSeverityKey(severity);
  const config = key ? SEVERITY_CONFIG[key] : null;
  if (!config) return null;
  return (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 border ${config.className}`}>
      {severity || config.label}
    </Badge>
  );
};

const DetailRow = ({ icon: Icon, label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-start gap-2 min-w-0">
      {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">{label}</p>
        <p className="text-xs text-gray-700 break-words">{value}</p>
      </div>
    </div>
  );
};

const CardSection = ({ children }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  if (items.length === 0) return null;
  return <div className="space-y-2.5 pt-2 border-t border-gray-100">{items}</div>;
};

const UserCardBody = ({ data, departmentNameMap }) => {
  const online = isUserLive(data);
  const duty = getDutyStatus(data);
  const department = getDepartmentDisplay(data, departmentNameMap);
  const coords = formatCoords(data.lat, data.lng);
  const lastUpdate = formatDateTime(data.timestamp) || (online ? 'Just now' : formatTimeAgo(data.timestamp));

  return (
    <>
      {!online && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 mt-1">
          Showing last known location
        </p>
      )}
      <CardSection>
        <DetailRow icon={Building2} label="Department" value={department} />
        <DetailRow icon={MapPin} label="Last Known Location" value={coords} />
        <DetailRow icon={Clock} label="Last Updated" value={lastUpdate} />
      </CardSection>
      <div className="flex flex-wrap gap-1.5 pt-2">
        <StatusBadge statusKey={online ? 'online' : 'offline'} />
        <StatusBadge statusKey={duty.key} label={duty.label} />
      </div>
    </>
  );
};

const TeamCardBody = ({ data }) => {
  const coords = formatCoords(data.lat, data.lng);
  const area = [data.team_name, data.crew_name].filter(Boolean).join(' · ');
  const lastUpdate = formatTimeAgo(data.timestamp) || formatDateTime(data.timestamp);

  return (
    <CardSection>
      <DetailRow icon={Users} label="Team" value={data.team_name} />
      <DetailRow icon={Users} label="Crew" value={data.crew_name} />
      <DetailRow icon={MapPin} label="Assigned Area" value={area} />
      <DetailRow icon={MapPin} label="Location" value={coords} />
      <DetailRow icon={Clock} label="Last Update" value={lastUpdate} />
      <DetailRow icon={Shield} label="Status" value={data.status && data.status !== 'active' ? data.status : null} />
    </CardSection>
  );
};

const IncidentRelationshipSection = ({ parentIncident, childIncidents, onFocusIncident }) => {
  if (!parentIncident && (!childIncidents || childIncidents.length === 0)) return null;

  return (
    <CardSection>
      {parentIncident && (
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium flex items-center gap-1">
            <GitBranch className="w-3 h-3" />
            Root Incident
          </p>
          <button
            type="button"
            onClick={() => onFocusIncident?.(parentIncident.id)}
            className="block w-full truncate text-left text-xs font-medium text-sky-700 hover:text-sky-900"
          >
            {parentIncident.title || parentIncident.incident_type || parentIncident.id}
          </button>
        </div>
      )}
      {childIncidents?.length > 0 && (
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium flex items-center gap-1">
            <GitBranch className="w-3 h-3" />
            Related Incidents ({childIncidents.length})
          </p>
          <div className="space-y-1 mt-0.5">
            {childIncidents.map((child) => (
              <button
                key={child.id}
                type="button"
                onClick={() => onFocusIncident?.(child.id)}
                className="block w-full truncate text-left text-xs font-medium text-sky-700 hover:text-sky-900"
              >
                {child.title || child.incident_type || child.id}
              </button>
            ))}
          </div>
        </div>
      )}
    </CardSection>
  );
};

const IncidentCardBody = ({ data }) => {
  const location = getLocationLabel(data);
  const projection = data?.map_projection;
  const mapPoint = projection?.official_coordinates || projection?.coordinates;
  const mapLat = mapPoint?.latitude ?? mapPoint?.lat;
  const mapLng = mapPoint?.longitude ?? mapPoint?.lng;
  const gpsLabel = mapLat != null && mapLng != null
    ? `${projection?.official ? 'Verified GPS' : 'Reporter GPS — unverified'}: ${Number(mapLat).toFixed(6)}, ${Number(mapLng).toFixed(6)}`
    : null;
  const reported = formatDateTime(data.created_at || data.timestamp);
  const statusKey = getIncidentStatusKey(data.status);
  const description = data.description?.trim();

  return (
    <>
      <CardSection>
        <DetailRow icon={AlertTriangle} label="Incident Type" value={data.incident_type} />
        <DetailRow icon={MapPin} label="Location" value={location} />
        {gpsLabel && <DetailRow icon={MapPin} label="Map point" value={gpsLabel} />}
        <DetailRow icon={Clock} label="Reported" value={reported} />
        {description && (
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Description</p>
            <p className="text-xs text-gray-600 line-clamp-3">{description}</p>
          </div>
        )}
      </CardSection>
      <div className="flex flex-wrap gap-1.5 pt-2">
        {data.is_exercise && (
          <Badge variant="outline" className="h-5 border border-sky-200 bg-sky-100 px-1.5 py-0 text-[10px] text-sky-700">
            {data.is_scenario ? 'Scenario' : 'Exercise'}
          </Badge>
        )}
        <StatusBadge statusKey={statusKey} />
        <SeverityBadge severity={data.severity} />
      </div>
    </>
  );
};

export const resolveEntityType = (data, markerType) => {
  if (markerType === 'incident') {
    return 'incident';
  }
  if (markerType === 'team') {
    if (data?.user_id && !data?.is_admin_team_marker) return 'responder';
    return 'team';
  }
  if (data?.is_admin_team_marker || (!data?.user_id && (data?.team_id || data?.crew_id))) {
    return 'team';
  }
  return 'user';
};

export const getEntityTitle = (data, entityType) => {
  switch (entityType) {
    case 'user':
    case 'responder':
      return data.user_name || data.name;
    case 'team':
      return data.team_name || data.crew_name || 'Team';
    case 'incident':
      return data.title || data.incident_type;
    default:
      return data.name || data.title;
  }
};

const MapEntityCard = ({
  entityType,
  data,
  actions,
  departmentNameMap,
  parentIncident,
  childIncidents,
  onFocusIncident,
}) => {
  const title = getEntityTitle(data, entityType);
  const isUserType = entityType === 'user' || entityType === 'responder';
  const isTeamType = entityType === 'team';
  const isIncidentType = entityType === 'incident';
  const online = isUserType ? isUserLive(data) : false;
  const departmentLabel = isUserType ? getDepartmentDisplay(data, departmentNameMap) : null;

  return (
    <div className="w-[240px] max-w-[280px] font-sans text-gray-900">
      <div className="flex items-start justify-between gap-2 mb-2">
        <EntityTypeBadge entityType={entityType} />
        {isUserType && <StatusBadge statusKey={online ? 'live' : 'offline'} />}
        {isIncidentType && <SeverityBadge severity={data.severity} />}
      </div>

      <div className="space-y-0.5 mb-1">
        <h3 className="font-semibold text-sm text-gray-900 leading-snug line-clamp-2">
          {title || ENTITY_TYPE_CONFIG[entityType]?.label}
        </h3>
        {departmentLabel && (
          <p className="text-[11px] text-gray-500 truncate">{departmentLabel}</p>
        )}
        {isIncidentType && data.incident_type && data.title && (
          <p className="text-[11px] text-gray-500 truncate">{data.incident_type}</p>
        )}
      </div>

      {isUserType && <UserCardBody data={data} departmentNameMap={departmentNameMap} />}
      {isTeamType && <TeamCardBody data={data} />}
      {isIncidentType && <IncidentCardBody data={data} />}
      {isIncidentType && (
        <IncidentRelationshipSection
          parentIncident={parentIncident}
          childIncidents={childIncidents}
          onFocusIncident={onFocusIncident}
        />
      )}

      {actions && (
        <div className="pt-3 mt-1 border-t border-gray-100 space-y-1.5">
          {actions}
        </div>
      )}
    </div>
  );
};

export const IncidentCardActions = ({ incidentId }) => (
  <Button asChild size="sm" className="w-full h-7 text-xs">
    <Link to={`/incident-details/${incidentId}`}>View Details</Link>
  </Button>
);

export const AssignedIncidentAction = ({ incidentId }) => (
  <Button asChild size="sm" variant="outline" className="w-full h-7 text-xs border-blue-200 hover:bg-blue-50 text-blue-700">
    <Link to={`/incident-details/${incidentId}`}>View Assigned Incident</Link>
  </Button>
);

export default MapEntityCard;
